import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Settings, IndianRupee, Save } from "lucide-react";

export default function AdminPricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (user && user.role === "admin") {
      (async () => {
        try { const { data } = await api.get("/admin/plans"); setPlans(data); }
        catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
        finally { setLoading(false); }
      })();
    } else { setLoading(false); }
  }, [user]);

  if (!user) return null;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const updateLocal = (id, key, value) =>
    setPlans((ps) => ps.map((p) => p.id === id ? { ...p, [key]: value } : p));

  const save = async (plan) => {
    setSaving(plan.id);
    try {
      await api.put(`/admin/plans/${plan.id}`, {
        amount: parseFloat(plan.amount),
        label: plan.label,
        days: parseInt(plan.days, 10),
        currency: plan.currency,
      });
      toast.success(`${plan.id} plan updated`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(null); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="admin-pricing-page">
      <Settings className="h-7 w-7 text-[#C85A44] mb-4" strokeWidth={1.25} />
      <p className="overline mb-3">Admin · Pricing</p>
      <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight">Manage subscription prices.</h1>
      <p className="mt-3 text-sm text-[#8C857B]">Changes go live instantly — users see the new price on the Premium page and during checkout.</p>

      <div className="mt-6 flex flex-wrap gap-2">
        <Link to="/admin/users" data-testid="admin-nav-users" className="rounded-full bg-[#2C2A28] hover:bg-black text-white px-4 py-2 text-xs transition">Users →</Link>
        <Link to="/admin/reports" data-testid="admin-nav-reports" className="rounded-full border border-[#E2DCD0] hover:bg-[#F2EFE9] px-4 py-2 text-xs transition">Reports →</Link>
      </div>

      {loading ? (
        <div className="text-center py-32 text-[#8C857B]">Loading…</div>
      ) : (
        <div className="mt-12 space-y-6">
          {plans.map((p) => (
            <div key={p.id} className="p-6 md:p-8 rounded-2xl bg-white border border-[#E2DCD0]" data-testid={`admin-plan-${p.id}`}>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="overline">{p.id}</p>
                  <h3 className="font-serif-display text-2xl mt-1">{p.label}</h3>
                </div>
                <div className="text-right">
                  <span className="font-serif-display text-4xl text-[#C85A44]">
                    {p.currency === "inr" ? "₹" : "$"}{p.amount}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Field label="Amount">
                  <div className="relative">
                    <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8C857B]" strokeWidth={1.5}/>
                    <input type="number" min={1} step="0.01" value={p.amount}
                      onChange={(e) => updateLocal(p.id, "amount", e.target.value)}
                      data-testid={`admin-amount-${p.id}`}
                      className="w-full rounded-xl border border-[#E2DCD0] bg-white pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A44]/30 transition" />
                  </div>
                </Field>
                <Field label="Currency">
                  <select value={p.currency} onChange={(e) => updateLocal(p.id, "currency", e.target.value)}
                    data-testid={`admin-currency-${p.id}`}
                    className="w-full rounded-xl border border-[#E2DCD0] bg-white px-3 py-2.5 text-sm">
                    <option value="inr">INR (₹)</option>
                    <option value="usd">USD ($)</option>
                  </select>
                </Field>
                <Field label="Days">
                  <input type="number" min={1} value={p.days}
                    onChange={(e) => updateLocal(p.id, "days", e.target.value)}
                    data-testid={`admin-days-${p.id}`}
                    className="w-full rounded-xl border border-[#E2DCD0] bg-white px-3 py-2.5 text-sm" />
                </Field>
                <Field label="Label">
                  <input value={p.label} onChange={(e) => updateLocal(p.id, "label", e.target.value)}
                    data-testid={`admin-label-${p.id}`}
                    className="w-full rounded-xl border border-[#E2DCD0] bg-white px-3 py-2.5 text-sm" />
                </Field>
              </div>
              <div className="mt-5 flex justify-end">
                <button onClick={() => save(p)} disabled={saving === p.id} data-testid={`admin-save-${p.id}`}
                  className="rounded-full bg-[#C85A44] hover:bg-[#A84A36] disabled:opacity-60 text-white px-5 py-2.5 text-sm inline-flex items-center gap-2 transition">
                  <Save className="h-4 w-4" strokeWidth={1.5}/> {saving === p.id ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-12 p-5 rounded-2xl bg-[#F2EFE9] border border-[#E2DCD0] text-sm text-[#5d574f]">
        <strong>💡 Tip:</strong> Lowering the price typically increases conversion in tier-2/3 cities. Test a ₹499 monthly tier for a week and compare paid signups.
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-[#8C857B] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
